/* ============================================================
   GS HELP WIDGET — behaviour (dependency-free)
   File: assets/gs-help-widget.js  (loaded deferred by sections/gs-help-widget.liquid)

   WHAT IT DOES
   1. PORTALS the widget to <body> (pack 16 Trap 1: position:fixed inside a
      transformed/contained Dawn section is clipped to the section, not the
      viewport — same fix the theme's trust/assured sheets use).
   2. Opens/closes the sheet with scroll-lock (shared `gs-sheet-open` flag,
      pack 16 rule R4/R7) + basic focus trap (Esc closes, Tab wraps, focus
      returns to the opener button) + aria-expanded bookkeeping.
   3. TEMPLATE-AWARE LIFT: on the product template it live-measures the
      sticky Add-to-Cart bar (.gyn-sticky.is-on) and keeps the button just
      above it — including the bar's optional offer strip — and lets the
      button drop back to the normal corner while the bar is hidden.
   4. FAQ accordion is native <details>; we only add "close the others"
      single-open behaviour on top.
   ============================================================ */
(function () {
  'use strict';

  /* Measure the visible sticky ATC bar — identical logic to the theme's
     other sheets (pack 16 recipe step 4) so all offsets agree. */
  function barHeight() {
    var bar = document.querySelector('.gyn-sticky.is-on');
    if (!bar) return 0;
    var r = bar.getBoundingClientRect();
    return (r.height && r.top < window.innerHeight) ? Math.round(r.height) : 0;
  }

  function init(root) {
    if (root.dataset.gsHelpInit === '1') return; /* guard against double init (editor reloads) */
    root.dataset.gsHelpInit = '1';

    /* PORTAL: escape the footer section's stacking context so our fixed
       button + sheet are truly viewport-relative (pack 16 rule R1). */
    if (root.parentNode !== document.body) document.body.appendChild(root);

    var btn = root.querySelector('[data-gs-help-btn]');
    var sheet = root.querySelector('[data-gs-help-sheet]');
    var box = root.querySelector('[data-gs-help-box]');
    if (!btn || !sheet || !box) return;

    var lastFocus = null;

    /* ---------- template-aware button lift (product pages only) ---------- */
    var isProduct = root.getAttribute('data-template') === 'product';
    var baseBottom = parseInt(root.getAttribute('data-bottom'), 10) || 16;
    if (isProduct) {
      var ticking = false;
      var place = function () {
        ticking = false;
        var h = barHeight();
        /* Bar visible -> hover 12px above its top edge. Bar hidden (top of
           page / drawer open) -> back to the merchant's normal offset. The
           CSS class fallback (--gs-help-lift setting) only applies before
           this script runs / without JS. */
        root.style.setProperty('--gs-help-pos', (h ? h + 12 : baseBottom) + 'px');
      };
      var onMove = function () { /* rAF-throttled: scroll fires constantly */
        if (!ticking) { ticking = true; requestAnimationFrame(place); }
      };
      window.addEventListener('scroll', onMove, { passive: true });
      window.addEventListener('resize', onMove);
      place();
      /* the bar animates in over .28s after load — re-measure once settled */
      window.addEventListener('load', function () { setTimeout(place, 600); });
    }

    /* ---------- open / close ---------- */
    function focusables() {
      return box.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])');
    }

    function open() {
      /* Re-append to the END of <body> on every open: the sticky ATC bar also
         sits at max z-index and re-appends itself, so at equal z the LAST
         sibling paints on top (pack 16 rule R6 — DOM order, not bigger numbers). */
      document.body.appendChild(root);
      /* R5: lift the mobile sheet above the visible sticky bar (shared var). */
      sheet.style.setProperty('--gs-sheet-offset', barHeight() + 'px');
      lastFocus = document.activeElement;
      sheet.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('gs-sheet-open'); /* scroll-lock, shared flag */
      box.focus(); /* box has tabindex="-1"; SR announces the dialog */
    }

    function close() {
      sheet.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('gs-sheet-open');
      /* return focus to whatever opened the sheet (a11y requirement) */
      if (lastFocus && document.contains(lastFocus)) lastFocus.focus(); else btn.focus();
    }

    btn.addEventListener('click', function () { sheet.hidden ? open() : close(); });
    /* scrim + X button both carry data-gs-help-close */
    root.querySelectorAll('[data-gs-help-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    /* Esc closes; Tab is trapped inside the sheet while open (basic trap) */
    sheet.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === box)) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    });

    /* ---------- FAQ accordion: only one answer open at a time ---------- */
    var faqs = root.querySelectorAll('details[data-gs-help-faq]');
    faqs.forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (!d.open) return;
        faqs.forEach(function (other) { if (other !== d) other.open = false; });
      });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-gs-help]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* THEME EDITOR SUPPORT: because we portal the widget out of its section,
     Shopify's editor can't swap it on setting changes — so on section:load we
     init the fresh copy, and on section:unload we remove the old portaled
     node from <body> ourselves (otherwise you'd get duplicates in Customize). */
  document.addEventListener('shopify:section:load', function (e) {
    var w = e.target.querySelector('[data-gs-help]');
    if (w) init(w);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (!id) return;
    document.querySelectorAll('body > [data-gs-help][data-section-id="' + id + '"]').forEach(function (n) {
      n.remove();
    });
    document.documentElement.classList.remove('gs-sheet-open'); /* never leave the page locked */
  });
})();
